import { Visibility, VisibilityOff } from '@mui/icons-material';
import { Localized, useTranslation } from 'dg-web-shared/lib/Localization.tsx';
import { useForm, useWatch } from 'react-hook-form';
import { ReactHookFormTextField } from 'dg-web-shared/common/components/material-ui/react-hook-form-fields/ReactHookFormTextField.tsx';
import {
    mandatoryFieldErrorMessage,
    minFieldErrorMessage,
} from 'dg-web-shared/common/components/translations/FormValidationErrorTexts.ts';
import { Box, Button, IconButton, InputAdornment, Stack } from '@mui/material';
import { useState } from 'react';
import {
    RequestMethod,
    RequestStatus,
    ServerRequestState,
    useServerSuccessEffect,
    useServerWrite,
} from 'dg-web-shared/lib/hooks/ServerStateHooks.ts';
import Alert from '@mui/material/Alert';
import {
    ConfirmationDialog,
    ConfirmationDialogVersions,
} from './ConfirmationDialog.tsx';
import {
    ModalVariant,
    OperatorRoutedModal,
} from '../../ui/modal/OperatorRoutedModal.tsx';
import { ParkingPortalLayoutWithHeader } from '../../mobile/layout/ParkingPortalLayoutWithHeader.tsx';
import { SubmitButton } from '../../ui/buttons/SubmitButton.tsx';
import { useNavigate } from 'react-router';

interface UpdatePasswordException {
    reason: UpdatePasswordExceptionReason;
}

const enum UpdatePasswordExceptionReason {
    INCORRECT_OLD_PASSWORD = 'INCORRECT_OLD_PASSWORD',
}

export const enum PasswordChangeFormFields {
    oldPassword = 'oldPassword',
    newPassword = 'newPassword',
    confirmNewPassword = 'confirmNewPassword',
}

export type PasswordChangeFormFieldValues = {
    [PasswordChangeFormFields.oldPassword]: string;
    [PasswordChangeFormFields.newPassword]: string;
    [PasswordChangeFormFields.confirmNewPassword]: string;
};

export function PasswordPage() {
    const navigate = useNavigate();
    const [successModalOpen, setSuccessModalOpen] = useState(false);
    const [updatePasswordState, updatePassword] = useServerWrite<
        PasswordChangeFormFieldValues,
        object,
        UpdatePasswordException
    >(() => ({
        url: `/ui-api/operator-account/password`,
        method: RequestMethod.PUT,
    }));
    const { control, reset, handleSubmit } =
        useForm<PasswordChangeFormFieldValues>({
            defaultValues: {
                [PasswordChangeFormFields.oldPassword]: '',
                [PasswordChangeFormFields.newPassword]: '',
                [PasswordChangeFormFields.confirmNewPassword]: '',
            },
        });
    useServerSuccessEffect(updatePasswordState, () => {
        setSuccessModalOpen(true);
        reset();
    });
    const mandatoryFieldMessage = useTranslation(mandatoryFieldErrorMessage);
    const minPasswordLength = 6;
    const minimumMessage = useTranslation(
        minFieldErrorMessage(minPasswordLength),
    );
    const confirmPasswordDoesNotMatch = useTranslation({
        de: 'Passwörter stimmen nicht überein',
        fr: 'Les mots de passe ne correspondent pas',
        it: 'Le password non corrispondono',
        en: 'Passwords do not match',
    });
    const [showOldPassword, setShowOldPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const newPassword = useWatch({
        control: control,
        name: PasswordChangeFormFields.newPassword,
    });
    const pending = updatePasswordState.status === RequestStatus.PENDING;
    return (
        <OperatorRoutedModal
            variant={ModalVariant.TINY}
            backUrl={'..'}
            render={() => (
                <>
                    <ParkingPortalLayoutWithHeader
                        title={
                            <Localized
                                de="Passwort ändern"
                                fr="Modifier le mot de passe"
                                it="Modifica password"
                                en="Change password"
                            />
                        }
                        backTo={'..'}
                    >
                        <Stack
                            sx={{
                                overflowY: 'auto',
                                display: 'flex',
                                flexDirection: 'column',
                                padding: '20px 24px',
                                flexGrow: 1,
                            }}
                            spacing={3}
                        >
                            <UpdateErrorExceptionMessage
                                updatePasswordState={updatePasswordState}
                            />
                            <ReactHookFormTextField
                                type={showOldPassword ? 'text' : 'password'}
                                autoComplete="current-password"
                                label={
                                    <Localized
                                        de="Altes Passwort"
                                        fr="Ancien mot de passe"
                                        it="Vecchia password"
                                        en="Old password"
                                    />
                                }
                                control={control}
                                name={PasswordChangeFormFields.oldPassword}
                                rules={{
                                    required: {
                                        value: true,
                                        message: mandatoryFieldMessage,
                                    },
                                    minLength: {
                                        value: 1,
                                        message: mandatoryFieldMessage,
                                    },
                                }}
                                slotProps={{
                                    input: {
                                        endAdornment: (
                                            <PasswordAdornment
                                                showPassword={showOldPassword}
                                                toggleShowPassword={() =>
                                                    setShowOldPassword(
                                                        prev => !prev,
                                                    )
                                                }
                                            />
                                        ),
                                    },
                                }}
                            />
                            <ReactHookFormTextField
                                type={showNewPassword ? 'text' : 'password'}
                                autoComplete="new-password"
                                label={
                                    <Localized
                                        de="Neues Passwort"
                                        fr="Nouveau mot de passe"
                                        it="Nuova password"
                                        en="New password"
                                    />
                                }
                                control={control}
                                name={PasswordChangeFormFields.newPassword}
                                rules={{
                                    required: {
                                        value: true,
                                        message: mandatoryFieldMessage,
                                    },
                                    minLength: {
                                        value: minPasswordLength,
                                        message: minimumMessage,
                                    },
                                }}
                                slotProps={{
                                    input: {
                                        endAdornment: (
                                            <PasswordAdornment
                                                showPassword={showNewPassword}
                                                toggleShowPassword={() =>
                                                    setShowNewPassword(
                                                        prev => !prev,
                                                    )
                                                }
                                            />
                                        ),
                                    },
                                }}
                            />
                            <ReactHookFormTextField
                                type={showConfirmPassword ? 'text' : 'password'}
                                autoComplete="new-password"
                                label={
                                    <Localized
                                        de="Neues Passwort bestätigen"
                                        fr="Confirmer le nouveau mot de passe"
                                        it="Conferma nuova password"
                                        en="Repeat new password"
                                    />
                                }
                                control={control}
                                name={
                                    PasswordChangeFormFields.confirmNewPassword
                                }
                                rules={{
                                    required: {
                                        value: true,
                                        message: mandatoryFieldMessage,
                                    },
                                    minLength: {
                                        value: minPasswordLength,
                                        message: minimumMessage,
                                    },
                                    validate: value =>
                                        value === newPassword ||
                                        confirmPasswordDoesNotMatch,
                                }}
                                slotProps={{
                                    input: {
                                        endAdornment: (
                                            <PasswordAdornment
                                                showPassword={
                                                    showConfirmPassword
                                                }
                                                toggleShowPassword={() =>
                                                    setShowConfirmPassword(
                                                        prev => !prev,
                                                    )
                                                }
                                            />
                                        ),
                                    },
                                }}
                            />
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                }}
                            >
                                <Button
                                    onClick={() => navigate('..')}
                                    variant="outlined"
                                    disabled={pending}
                                >
                                    <Localized
                                        de="Abbrechen"
                                        fr="Annuler"
                                        it="Annulla"
                                        en="Cancel"
                                    />
                                </Button>
                                <SubmitButton
                                    sx={{ marginLeft: '5px' }}
                                    loading={pending}
                                    onClick={handleSubmit(updatePassword)}
                                    label={
                                        <Localized
                                            de="Speichern"
                                            fr="Sauvegarder"
                                            it="Salva"
                                            en="Save"
                                        />
                                    }
                                />
                            </Box>
                            <ConfirmationDialog
                                open={successModalOpen}
                                title={
                                    <Localized
                                        de="Passwort geändert"
                                        fr="Mot de passe modifié"
                                        it="Password modificata"
                                        en="Password changed"
                                    />
                                }
                                onConfirm={() => {
                                    navigate('..');
                                    setSuccessModalOpen(false);
                                }}
                                confirmLabel={
                                    <Localized
                                        de="OK"
                                        fr="OK"
                                        it="OK"
                                        en="OK"
                                    />
                                }
                                dialogVersion={
                                    ConfirmationDialogVersions.SUCCESS
                                }
                            >
                                <p>
                                    <Localized
                                        de="Das Passwort wurde erfolgreich verändert."
                                        fr="Le mot de passe a été modifié avec succès."
                                        it="La password è stata modificata con successo."
                                        en="The password was changed successfully."
                                    />
                                </p>
                            </ConfirmationDialog>
                        </Stack>
                    </ParkingPortalLayoutWithHeader>
                </>
            )}
        />
    );
}

function UpdateErrorExceptionMessage({
    updatePasswordState,
}: {
    updatePasswordState: ServerRequestState<object, UpdatePasswordException>;
}) {
    if (updatePasswordState.status !== RequestStatus.ERROR) {
        return null;
    }
    switch (updatePasswordState.data?.reason) {
        case UpdatePasswordExceptionReason.INCORRECT_OLD_PASSWORD:
            return (
                <Alert severity="error">
                    <Localized
                        de="Das alte Passwort ist nicht korrekt."
                        fr="L'ancien mot de passe est incorrect."
                        it="La vecchia password non è corretta."
                        en="The old password is incorrect."
                    />
                </Alert>
            );
    }
    return (
        <Alert severity="error">
            <Localized
                de="Ein unerwarteter Fehler ist aufgetreten."
                fr="Une erreur inattendue s'est produite."
                it="Si è verificato un errore imprevisto."
                en="An unexpected error has occurred."
            />
        </Alert>
    );
}

function PasswordAdornment({
    showPassword,
    toggleShowPassword,
}: {
    showPassword: boolean;
    toggleShowPassword: () => void;
}) {
    return (
        <InputAdornment position="end">
            <IconButton onClick={toggleShowPassword} edge="end">
                {showPassword ? <VisibilityOff /> : <Visibility />}
            </IconButton>
        </InputAdornment>
    );
}
